import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { waLink, telLink, track, newIdempotencyKey, EMPLOYMENT_TYPES } from "../../lib/site";

const utms = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "", utm_term: p.get("utm_term") || "",
    utm_content: p.get("utm_content") || "",
  };
};

export default function LeadForm({ defaultProduct = "", source = "website", title = "Tell us what you need. We'll help you find the right option.", compact = false }) {
  const { settings, products } = useSite();
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", loan_type: defaultProduct, amount: "",
    employment: "", city: "", preferred_contact: "call",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const idemKey = useMemo(newIdempotencyKey, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/leads", {
        ...form, amount: Number(form.amount) || 0, consent: true, kind: "loan",
        source, page: window.location.pathname, idempotency_key: idemKey, ...utms(),
      });
      track("lead_submit", { lead_id: data.lead_id, product: form.loan_type });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="card-site p-8 sm:p-10 text-center">
        <span className="mx-auto w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={28} /></span>
        <h3 className="mt-5 font-heading text-2xl font-bold text-brand-dark">Thank You!</h3>
        <p className="mt-2 text-sm text-brand-muted leading-relaxed">Your enquiry has been submitted successfully.<br />Our BharatBest Fintech team will review your enquiry and contact you.</p>
        <p className="mt-3 text-sm text-brand-muted">Lead ID: <span className="font-heading font-extrabold text-brand-red">{done.lead_id}</span></p>
        <p className="mt-4 text-xs text-brand-muted leading-relaxed">Final eligibility, approval and terms are determined by the respective lending partner after verification.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={waLink(settings, settings?.whatsapp_templates?.default)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { lead_id: done.lead_id })} data-testid="success-whatsapp-btn" className="btn-outline !border-[#1FA855]/40 !text-[#1FA855]">WhatsApp Advisor</a>
          <a href={telLink(settings)} onClick={() => track("phone_click", { lead_id: done.lead_id })} data-testid="success-call-btn" className="btn-outline">Call Now</a>
          <Link to="/" data-testid="success-home-btn" className="btn-primary">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quick-enquiry-form" className="card-site p-6 sm:p-8">
      {title && <h3 className="font-heading text-xl sm:text-2xl font-bold text-brand-dark mb-6">{title}</h3>}
      <div className={`grid gap-4 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
        <input required data-testid="enquiry-name-input" className="input-site" placeholder="Full Name *" value={form.name} onChange={set("name")} aria-label="Full name" />
        <input required data-testid="enquiry-mobile-input" className="input-site" placeholder="Mobile Number *" value={form.mobile} onChange={set("mobile")} inputMode="tel" aria-label="Mobile number" />
        <input data-testid="enquiry-email-input" type="email" className="input-site" placeholder="Email" value={form.email} onChange={set("email")} aria-label="Email" />
        <select required data-testid="enquiry-loan-type-select" className="input-site" value={form.loan_type} onChange={set("loan_type")} aria-label="Loan type">
          <option value="" disabled>Select Loan Type *</option>
          {(products.length ? products.map((p) => p.name) : []).map((t) => <option key={t} value={t}>{t}</option>)}
          <option value="Other">Other</option>
        </select>
        <input required data-testid="enquiry-amount-input" className="input-site" placeholder="Required Amount (₹) *" value={form.amount} onChange={set("amount")} inputMode="numeric" aria-label="Required amount" />
        <select data-testid="enquiry-employment-select" className="input-site" value={form.employment} onChange={set("employment")} aria-label="Employment type">
          <option value="" disabled>Employment Type</option>
          {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input data-testid="enquiry-city-input" className="input-site" placeholder="City" value={form.city} onChange={set("city")} aria-label="City" />
        <select data-testid="enquiry-contact-select" className="input-site" value={form.preferred_contact} onChange={set("preferred_contact")} aria-label="Preferred contact method">
          <option value="call">Preferred: Call</option>
          <option value="whatsapp">Preferred: WhatsApp</option>
          <option value="email">Preferred: Email</option>
        </select>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-brand-muted" data-testid="enquiry-consent-text">{settings.consent_text}</p>
      <button type="submit" disabled={loading} data-testid="enquiry-submit-btn" className="btn-primary mt-5 w-full sm:w-auto">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <>Check My Options <ArrowRight size={15} /></>}
      </button>
    </form>
  );
}
